"use client";

import type { AcademyCategory } from "@nyntax/contracts/schemas/course";
import { cn } from "@nyntax/design-system/lib/utils";
import { useEffect, useRef, useState } from "react";

/** A different colour per category, so covers that are missing do not all look alike. */
const CATEGORY_GRADIENT: Record<AcademyCategory, string> = {
  academics: "from-blue-600 to-indigo-800",
  "exam-prep": "from-violet-600 to-purple-900",
  language: "from-emerald-500 to-teal-800",
  technology: "from-cyan-500 to-blue-800",
  business: "from-amber-500 to-orange-700",
  design: "from-pink-500 to-rose-800",
  "personal-development": "from-orange-500 to-red-700",
  other: "from-slate-500 to-slate-800",
};

interface CourseCoverProps {
  className?: string;
  /**
   * What to draw when there is no cover: the category's colour and the title.
   * Without it the placeholder is a plain soft gradient.
   */
  fallback?: { category: AcademyCategory; title: string };
  /** Leave the title off the coloured card, for a thumbnail too small to read it. */
  fallbackTitle?: boolean;
  /** The cover address, or null for a course without one. */
  src: string | null;
}

/**
 * A course cover with a soft gradient for a course that has none, or whose
 * image cannot be loaded (a missing file must not show a broken-image icon).
 *
 * @param props - The cover address and extra classes.
 * @returns The image, or the gradient placeholder.
 */
export const CourseCover = ({
  className,
  fallback,
  fallbackTitle = true,
  src,
}: CourseCoverProps) => {
  const [failed, setFailed] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  // A server-rendered image can fail before React attaches `onError`, so
  // look at it once on mount as well.
  useEffect(() => {
    const image = imageRef.current;

    if (image?.complete && image.naturalWidth === 0) {
      setFailed(true);
    }
  }, [src]);

  if ((src === null || failed) && fallback) {
    return (
      <div
        aria-hidden="true"
        className={cn(
          "relative flex aspect-video w-full items-end overflow-hidden bg-gradient-to-br p-4",
          CATEGORY_GRADIENT[fallback.category],
          className
        )}
      >
        <div className="absolute -top-10 -right-10 size-40 rounded-full bg-white/10" />
        <div className="absolute -bottom-16 left-8 size-44 rounded-full bg-black/10" />
        {fallbackTitle ? (
          <p className="relative line-clamp-3 text-lg leading-tight font-semibold tracking-tight text-white drop-shadow-sm">
            {fallback.title}
          </p>
        ) : null}
      </div>
    );
  }

  if (src === null || failed) {
    return (
      <div
        aria-hidden="true"
        className={cn(
          "from-primary/35 via-muted to-primary/10 aspect-video w-full bg-gradient-to-br",
          className
        )}
      />
    );
  }

  return (
    // The cover route redirects to a short-lived file URL, so the browser
    // loads it directly instead of through the image optimizer.
    <img
      alt=""
      className={cn("aspect-video w-full object-cover", className)}
      onError={() => {
        setFailed(true);
      }}
      ref={imageRef}
      src={src}
    />
  );
};
